import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Grid, Chip, Avatar, Divider, Stack, IconButton, Tooltip,
  CircularProgress, useTheme, useMediaQuery,
  Popper, Paper, List, ListItemButton, ListItemAvatar, ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ReplyIcon from '@mui/icons-material/Reply';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import NotesIcon from '@mui/icons-material/Notes';
import DeleteIcon from '@mui/icons-material/Delete';
import ForumIcon from '@mui/icons-material/Forum';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';
import InfoIcon from '@mui/icons-material/Info';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import axios from 'axios';
import { projectAPI, statusAPI, ticketAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE_URL = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

const STATUS_META = {
  planning: { label: 'Planning', color: '#7B1FA2' },
  active: { label: 'Active', color: '#2E7D32' },
  on_hold: { label: 'On Hold', color: '#E65100' },
  completed: { label: 'Completed', color: '#1565C0' },
  cancelled: { label: 'Cancelled', color: '#C62828' },
};
const ROLE_COLORS = {
  admin: '#FF6B35', support_team: '#4472C4', team_member: '#2E7D32',
  qa_team: '#7B1FA2', higher_authority: '#C62828',
};
const TASK_STATUS_COLORS = {
  'In Progress': { bg: '#1565C018', color: '#1565C0' },
  'Completed':   { bg: '#2E7D3218', color: '#2E7D32' },
  'Blocked':     { bg: '#C6282818', color: '#C62828' },
  'On Hold':     { bg: '#E6510018', color: '#E65100' },
  'Review':      { bg: '#7B1FA218', color: '#7B1FA2' },
};
const TICKET_STATUS_COLORS = {
  open: { bg: '#C6282818', color: '#C62828' },
  in_progress: { bg: '#E6510018', color: '#E65100' },
  resolved: { bg: '#1565C018', color: '#1565C0' },
  closed: { bg: '#42424218', color: '#424242' },
};

// Formats a UTC timestamp string as date + time in India Standard Time.
function formatIST(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  const datePart = d
    .toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric' })
    .replace(/\//g, '-');
  const timePart = d.toLocaleTimeString('en-US', {
    timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true,
  });
  return `${datePart}, ${timePart} IST`;
}

// Highlights any "@Full Name" substrings that match a real project member.
function renderMessageWithMentions(text, memberNames) {
  if (!text || memberNames.length === 0) return text;
  const sorted = [...memberNames].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(
    '@(' + sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')\\b',
    'g'
  );
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <Box key={key++} component="span" sx={{ color: '#1565C0', fontWeight: 700, bgcolor: '#1565C012', borderRadius: 1, px: 0.4 }}>
        {match[0]}
      </Box>
    );
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

// Turns the flat messages array (each with a reply_to_id) into a nested
// reply tree: top-level messages at the root, each with a `children` array
// of the messages that replied directly to it, and so on recursively.
function buildMessageTree(messages) {
  const byId = {};
  messages.forEach((m) => { byId[m.id] = { ...m, children: [] }; });
  const roots = [];
  messages.forEach((m) => {
    const node = byId[m.id];
    if (m.reply_to_id && byId[m.reply_to_id]) {
      byId[m.reply_to_id].children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

function SectionCard({ icon: Icon, title, action, children }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Icon sx={{ color: '#1E3A5F', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
          </Stack>
          {action}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

export default function ProjectViewPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const token = localStorage.getItem('token');

  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dailyStatuses, setDailyStatuses] = useState([]);
  const [openTickets, setOpenTickets] = useState([]);
  const [remarks, setRemarks] = useState([]);
  const [remarksDenied, setRemarksDenied] = useState(false);
  const [newRemark, setNewRemark] = useState('');
  const [remarkSaving, setRemarkSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const messageInputRef = useRef(null);

  const canEdit = ['admin', 'support_team', 'qa_team', 'team_member'].includes(user?.role);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [projectRes, membersRes, messagesRes, filesRes, statusRes, ticketsRes] = await Promise.all([
        projectAPI.getOne(id),
        projectAPI.getMembers(id),
        projectAPI.getMessages(id),
        axios.get(`${API_URL}/projects/${id}/files`, { headers: { Authorization: `Bearer ${token}` } }),
        statusAPI.getAll(id),
        ticketAPI.getForProject(id, { status: 'open' }),
      ]);
      setProject(projectRes.data);
      setMembers(membersRes.data);
      setMessages(messagesRes.data);
      setFiles(filesRes.data);
      setDailyStatuses(statusRes.data || []);
      setOpenTickets(ticketsRes.data || []);
    } catch (err) {
      showToast('Failed to load project', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadRemarks = useCallback(() => {
    projectAPI.getRemarks(id)
      .then((res) => { setRemarks(res.data); setRemarksDenied(false); })
      .catch((err) => { if (err.response?.status === 403) setRemarksDenied(true); });
  }, [id]);

  useEffect(() => { loadRemarks(); }, [loadRemarks]);

  const handleAddRemark = async () => {
    const text = newRemark.trim();
    if (!text) return;
    setRemarkSaving(true);
    try {
      await projectAPI.addRemark(id, { remark: text });
      setNewRemark('');
      loadRemarks();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to add remark', 'error');
    } finally {
      setRemarkSaving(false);
    }
  };

  const handleDeleteRemark = async (remarkId) => {
    if (!window.confirm('Delete this remark?')) return;
    try {
      await projectAPI.deleteRemark(id, remarkId);
      loadRemarks();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete remark', 'error');
    }
  };

  const fmtRemarkTime = (d) => {
    try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      const mentions = members
        .filter((m) => m.member?.name && new RegExp('@' + m.member.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b').test(newMessage.trim()))
        .map((m) => m.member.id);
      await projectAPI.addMessage(id, { message: newMessage.trim(), mentions, reply_to_id: replyingTo?.id || null });
      setNewMessage('');
      setReplyingTo(null);
      const res = await projectAPI.getMessages(id);
      setMessages(res.data);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send message', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const startReply = (m) => {
    setReplyingTo(m);
    setTimeout(() => messageInputRef.current?.focus(), 0);
  };

  const cancelReply = () => setReplyingTo(null);

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    const match = textBeforeCursor.match(/@([a-zA-Z ]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionAnchor(e.target);
    } else {
      setMentionAnchor(null);
      setMentionQuery('');
    }
  };

  const selectMention = (name) => {
    const input = messageInputRef.current;
    const cursorPos = input ? input.selectionStart : newMessage.length;
    const textBeforeCursor = newMessage.slice(0, cursorPos);
    const textAfterCursor = newMessage.slice(cursorPos);
    const newTextBefore = textBeforeCursor.replace(/@([a-zA-Z ]*)$/, '@' + name + ' ');
    const updated = newTextBefore + textAfterCursor;
    setNewMessage(updated);
    setMentionAnchor(null);
    setMentionQuery('');
    setTimeout(() => {
      if (input) {
        const pos = newTextBefore.length;
        input.focus();
        input.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!project) return null;

  const statusMeta = STATUS_META[project.status] || STATUS_META.planning;
  const categories = (project.category || '').split(',').map((c) => c.trim()).filter(Boolean);
  const memberNames = members.map((m) => m.member?.name).filter(Boolean);
  const mentionCandidates = members
    .filter((m) => m.member?.name?.toLowerCase().includes(mentionQuery.toLowerCase()))
    .slice(0, 6);

  // Renders one message plus all of its replies nested underneath it,
  // indented and connected by a left border so the conversation reads as
  // a reply tree rather than a flat list.
  const renderMessageNode = (m, depth) => (
    <Box
      key={m.id}
      sx={{
        pl: depth > 0 ? 2 : 0,
        borderLeft: depth > 0 ? '2px solid' : 'none',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Avatar
          sx={{
            width: 32, height: 32, flexShrink: 0, fontSize: '0.75rem', fontWeight: 700,
            bgcolor: (ROLE_COLORS[m.author?.role] || '#888') + '30',
            color: ROLE_COLORS[m.author?.role] || '#888',
          }}
        >
          {m.author?.name?.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.author?.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatIST(m.created_at)}
              {m.edited ? ' · edited' : ''}
            </Typography>
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" sx={{ mt: 0.3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {renderMessageWithMentions(m.message, memberNames)}
            </Typography>
            <Tooltip title="Reply">
              <IconButton size="small" sx={{ flexShrink: 0 }} onClick={() => startReply(m)}>
                <ReplyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      {m.children && m.children.length > 0 && (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {m.children.map((c) => renderMessageNode(c, depth + 1))}
        </Stack>
      )}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')} color="inherit">
          Back
        </Button>
        {canEdit && (
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/projects/${id}/edit`)}>
            Edit Project
          </Button>
        )}
      </Box>

      {/* ── Project Details ── */}
      <SectionCard icon={InfoIcon} title={project.name}>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={statusMeta.label}
            size="small"
            sx={{ bgcolor: statusMeta.color + '18', color: statusMeta.color, fontWeight: 700 }}
          />
          {categories.map((c) => (
            <Chip key={c} label={c} size="small" variant="outlined" />
          ))}
        </Stack>

        {project.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {project.description}
          </Typography>
        )}

        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem' }}>
              Start Date
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.3 }}>{project.start_date || '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem' }}>
              End Date
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.3 }}>{project.end_date || '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem' }}>
              TAT Date
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.3 }}>{project.tat_date || '—'}</Typography>
          </Grid>
        </Grid>
      </SectionCard>

      {/* ── Team Members ── */}
      <SectionCard icon={PeopleIcon} title={`Team Members (${members.length})`}>
        {members.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No members assigned yet.</Typography>
        ) : (
          <Grid container spacing={1.5}>
            {members.map((m) => (
              <Grid item xs={12} sm={6} key={m.id}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ p: 1.2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                >
                  <Avatar
                    sx={{
                      width: 34, height: 34, fontSize: '0.8rem', fontWeight: 700,
                      bgcolor: (ROLE_COLORS[m.member?.role] || '#888') + '30',
                      color: ROLE_COLORS[m.member?.role] || '#888',
                    }}
                  >
                    {m.member?.name?.charAt(0)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>{m.member?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.role_in_project || m.member?.role?.replace(/_/g, ' ')}</Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        )}
      </SectionCard>

      {/* ── Files ── */}
      <SectionCard icon={FolderIcon} title={`Files (${files.length})`}>
        {files.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No files uploaded yet.</Typography>
        ) : (
          <Stack spacing={1}>
            {files.map((f) => (
              <Box
                key={f.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
              >
                <AttachFileIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{f.original_name || f.filename}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Uploaded by {f.uploader?.name || 'Unknown'}
                  </Typography>
                </Box>
                <Tooltip title="Download">
                  <IconButton size="small" onClick={() => window.open(`${BASE_URL}/uploads/${f.filename}`, '_blank')}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}
      </SectionCard>

      {/* ── Daily Status ── */}
      <SectionCard icon={EventNoteIcon} title={`Daily Status (${dailyStatuses.length})`}>
        {dailyStatuses.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No status updates yet.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {dailyStatuses.map((s) => {
              const taskStatus = s.tasks_planned || 'In Progress';
              const style = TASK_STATUS_COLORS[taskStatus] || TASK_STATUS_COLORS['In Progress'];
              return (
                <Box key={s.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                    <Avatar
                      sx={{
                        width: 32, height: 32, fontSize: '0.78rem', fontWeight: 700,
                        bgcolor: (ROLE_COLORS[s.author?.role] || '#888') + '30',
                        color: ROLE_COLORS[s.author?.role] || '#888',
                      }}
                    >
                      {s.author?.name?.charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                        {s.author?.name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.date}{s.start_time && s.end_time ? ` · ${s.start_time} - ${s.end_time}` : s.hours_worked ? ` · ${s.hours_worked}h` : ''}
                      </Typography>
                    </Box>
                    <Chip
                      label={taskStatus}
                      size="small"
                      sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700 }}
                    />
                    <Chip
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: '13px !important' }} />}
                      label={`${s.completion_percentage || 0}%`}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                  {s.tasks_completed && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {s.tasks_completed}
                    </Typography>
                  )}
                  {s.blockers && s.blockers !== 'None' && (
                    <Typography variant="body2" sx={{ mt: 0.5, color: 'error.main', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      ⚠️ {s.blockers}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      {/* ── Open Tickets ── */}
      <SectionCard
        icon={ConfirmationNumberIcon}
        title={`Open Tickets (${openTickets.length})`}
        action={
          <Button size="small" onClick={() => navigate('/tickets')}>
            View All
          </Button>
        }
      >
        {openTickets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No open tickets for this project.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {openTickets.map((t) => {
              const style = TICKET_STATUS_COLORS[t.status] || TICKET_STATUS_COLORS.open;
              const assigneeNames = (t.assignees || []).map((a) => a.member?.name).filter(Boolean);
              return (
                <Box
                  key={t.id}
                  onClick={() => navigate('/tickets', { state: { openTicket: { projectId: Number(id), ticketId: t.id } } })}
                  sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                          {t.ticket_number}
                        </Typography>
                        <Chip
                          size="small"
                          label={(t.status || '').replace('_', ' ')}
                          sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, textTransform: 'capitalize', fontSize: '0.7rem' }}
                        />
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.3 }}>
                        {t.title}
                      </Typography>
                      {t.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {t.description}
                        </Typography>
                      )}
                      {assigneeNames.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Assigned to: {assigneeNames.join(', ')}
                        </Typography>
                      )}
                    </Box>
                    {t.est_end_date && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                          Est. End
                        </Typography>
                        <Typography variant="body2">{t.est_end_date}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      {/* ── Communication ── */}
      <SectionCard icon={NotesIcon} title={`Remarks (${remarks.length})`}>
        {remarksDenied ? (
          <Typography variant="body2" color="text.secondary">Only members assigned to this project can view and add remarks.</Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
              <TextField fullWidth multiline minRows={2} maxRows={6} size="small" placeholder="Write a remark for this project…"
                value={newRemark} onChange={(e) => setNewRemark(e.target.value)} />
              <Button variant="contained" onClick={handleAddRemark} disabled={remarkSaving || !newRemark.trim()}
                startIcon={remarkSaving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />} sx={{ whiteSpace: 'nowrap', mt: 0.3 }}>
                Add Remark
              </Button>
            </Box>
            {remarks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No remarks yet.</Typography>
            ) : (
              <Stack spacing={1.2}>
                {remarks.map((r) => (
                  <Box key={r.id} sx={{ display: 'flex', gap: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', fontWeight: 700 }}>{r.author?.name?.charAt(0)}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        <b>{r.author?.name || 'User'}</b> · {fmtRemarkTime(r.created_at)}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.remark}</Typography>
                    </Box>
                    {(r.user_id === user?.id || user?.role === 'admin') && (
                      <Tooltip title="Delete remark">
                        <IconButton size="small" onClick={() => handleDeleteRemark(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )}
      </SectionCard>

      <SectionCard icon={ForumIcon} title={`Communication (${messages.length})`}>
        <Stack spacing={2} sx={{ mb: 2.5, maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
          {messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
              <ForumIcon sx={{ fontSize: 32, opacity: 0.15, display: 'block', mx: 'auto', mb: 1 }} />
              <Typography color="text.secondary" variant="body2">No messages yet. Start the conversation.</Typography>
            </Box>
          ) : (
            buildMessageTree(messages).map((m) => renderMessageNode(m, 0))
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {replyingTo && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
              mb: 1, p: 1, pl: 1.5, borderLeft: '3px solid', borderColor: 'primary.main',
              bgcolor: 'action.hover', borderRadius: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block' }}>
                Replying to {replyingTo.author?.name || 'Unknown'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 420 }}>
                {replyingTo.message}
              </Typography>
            </Box>
            <IconButton size="small" onClick={cancelReply}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Stack direction="row" spacing={1.5} alignItems="flex-end">
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            placeholder="Write a message to the project team... (@ to mention)"
            value={newMessage}
            onChange={handleMessageChange}
            inputRef={messageInputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !mentionAnchor) {
                e.preventDefault();
                handleSendMessage();
              }
              if (e.key === 'Escape') { setMentionAnchor(null); if (replyingTo) cancelReply(); }
            }}
          />
          <Button
            variant="contained"
            endIcon={<SendIcon fontSize="small" />}
            disabled={!newMessage.trim() || sendingMessage}
            onClick={handleSendMessage}
            sx={{ flexShrink: 0 }}
          >
            Send
          </Button>
        </Stack>

        <Popper
          open={Boolean(mentionAnchor) && mentionCandidates.length > 0}
          anchorEl={mentionAnchor}
          placement="top-start"
          sx={{ zIndex: 1400 }}
        >
          <Paper sx={{ width: 260, maxHeight: 220, overflowY: 'auto', mt: -1, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', borderRadius: 2 }}>
            <List dense disablePadding>
              {mentionCandidates.map((m) => (
                <ListItemButton key={m.member?.id} onMouseDown={(e) => { e.preventDefault(); selectMention(m.member?.name); }}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: (ROLE_COLORS[m.member?.role] || '#888') + '30', color: ROLE_COLORS[m.member?.role] || '#888', fontSize: '0.7rem', fontWeight: 700 }}>
                      {m.member?.name?.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={m.member?.name} secondary={m.member?.role?.replace(/_/g, ' ')} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '0.7rem', textTransform: 'capitalize' }} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      </SectionCard>
    </Box>
  );
}